import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, X } from 'lucide-react';
import { useCanonical } from '../../hooks/useCanonical';
import { useAuthStore } from '../../store/authStore';
import { useAuthLayoutContext } from '../../components/layout/AuthLayout';
import { authApi } from '../../services/api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import CapabilitiesContent from '../../components/auth/CapabilitiesContent';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().default(false),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  useCanonical('/login');
  const navigate = useNavigate();
  const { setAuth, setPermissions, setOrganizations, setCurrentOrganization, setOrganizationModules, setRememberMe } = useAuthStore();
  const { registerVerticalClickHandler } = useAuthLayoutContext();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [capabilitiesSection, setCapabilitiesSection] = useState<string | undefined>(undefined);

  // Register handler so AuthLayout's vertical tiles can open the modal pre-scrolled
  useEffect(() => {
    registerVerticalClickHandler((sectorId: string) => {
      setCapabilitiesSection(sectorId);
      setCapabilitiesOpen(true);
    });
  }, [registerVerticalClickHandler]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError('');
      const response = await authApi.login(data);
      
      // Persist remember-me preference, then set auth data
      setRememberMe(data.rememberMe);
      setAuth(response.data.user, response.data.token);
      
      // Set V0.2 multi-tenant data
      if (response.data.permissions) {
        setPermissions(response.data.permissions);
      }

      if (response.data.enabled_modules) {
        setOrganizationModules(response.data.enabled_modules);
      }
      
      if (response.data.organizations && response.data.organizations.length > 0) {
        setOrganizations(response.data.organizations);
        // Set first organization as current
        setCurrentOrganization(response.data.organizations[0].id);
      }
      
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modal = capabilitiesOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setCapabilitiesOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl h-[88vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Nabhos Platform Capabilities</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">The Operating System for the Sky — five industry verticals</p>
              </div>
              <button
                type="button"
                onClick={() => setCapabilitiesOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close capabilities overview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Capabilities content — inline React, inherits app theme */}
            <div className="flex-1 overflow-hidden bg-white dark:bg-gray-900 flex flex-col">
              <CapabilitiesContent defaultSection={capabilitiesSection} />
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
    {modal}
    <div>
      <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-0.5">
        Welcome back
      </h2>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
        Sign in to your Nabhos workspace
      </p>

      {error && (
        <Alert type="error" className="mb-3">
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <Input
          label="Work email"
          type="email"
          placeholder="you@yourorganisation.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="Password"
          type="password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 accent-primary-600 cursor-pointer"
              {...register('rememberMe')}
            />
            <span className="text-xs text-gray-700 dark:text-gray-200">Remember me</span>
          </label>
          <Link
            to="/forgot-password"
            className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={loading}>
          Sign in to Nabhos
        </Button>
      </form>

      <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
        New to Nabhos?{' '}
        <Link
          to="/register"
          className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          Request access
        </Link>
      </p>

      {/* Contact Section for Sales/Enquiries */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-1">
                Interested in Nabhos?
              </h3>
              <p className="text-xs text-blue-800 dark:text-blue-200 mb-3">
                For demos, pricing enquiries, or to discuss solutions for your organisation:
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="mailto:admin@nabhos.com"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact: admin@nabhos.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// Made with Bob
